import React from 'react';

/** Human labels for the event vocabulary emitted by the mentor app. */
export const EVENT_LABELS = {
  login: 'Login',
  logout: 'Logout',
  session_start: 'Session start',
  app_foreground: 'App opened',
  app_background: 'App backgrounded',
  screen_view: 'Screen view',
  view_student: 'Viewed student',
  view_heatmap: 'Viewed heatmap',
  view_batch_report: 'Viewed batch report',
  open_chat: 'Opened chat',
  send_message: 'Sent message',
  run_ai_agent: 'Ran AI agent',
  export: 'Exported report',
};

export const HIERARCHY_LABELS = {
  MENTOR: 'Mentor',
  HEAD_MENTOR: 'Head Mentor',
  CLASS_HEAD: 'Class Head',
  CHIEF_MENTOR: 'Chief Mentor',
  ADMIN: 'Admin',
};

export const eventLabel = (name) => EVENT_LABELS[name] || name;

export const formatNumber = (n) => (n === null || n === undefined ? '--' : Number(n).toLocaleString('en-IN'));

export const formatDate = (value) => {
  if (!value) return '--';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

export const formatDateTime = (value) => {
  if (!value) return '--';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

/** "3 days ago" style relative age, used for last-seen columns. */
export const formatRelative = (value) => {
  if (!value) return 'Never';
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

/** `null` means "no timing data in this window" — render it as unknown, not zero. */
export const formatMinutes = (value) => (value === null || value === undefined ? '--' : `${value} min`);

/**
 * Multi-series line chart drawn as inline SVG.
 *
 * The client has no charting dependency — the hierarchy org chart is hand-rolled
 * SVG too — so this follows the same approach rather than pulling one in.
 */
export const LineChart = ({ data, series, height = 220, xKey = 'date' }) => {
  if (!data || data.length === 0) {
    return <div className="analytics-empty-chart">No activity in this range</div>;
  }

  const width = 900;
  const pad = { top: 16, right: 16, bottom: 28, left: 48 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const maxValue = Math.max(
    1,
    ...series.flatMap((s) => data.map((d) => Number(d[s.key]) || 0)),
  );

  // A single point has no span to divide by; centre it instead.
  const xAt = (i) => (data.length === 1
    ? pad.left + plotW / 2
    : pad.left + (i / (data.length - 1)) * plotW);
  const yAt = (v) => pad.top + plotH - ((Number(v) || 0) / maxValue) * plotH;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(maxValue * f));
  const labelEvery = Math.max(1, Math.ceil(data.length / 8));

  return (
    <div className="analytics-chart-scroll">
      <svg
        className="analytics-line-chart"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Trend chart: ${series.map((s) => s.label).join(', ')}`}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              className="analytics-grid-line"
              x1={pad.left} x2={width - pad.right}
              y1={yAt(t)} y2={yAt(t)}
            />
            <text className="analytics-axis-label" x={pad.left - 8} y={yAt(t) + 4} textAnchor="end">
              {formatNumber(t)}
            </text>
          </g>
        ))}

        {series.map((s) => (
          <polyline
            key={s.key}
            className="analytics-line"
            style={{ stroke: s.color }}
            points={data.map((d, i) => `${xAt(i)},${yAt(d[s.key])}`).join(' ')}
          />
        ))}

        {series.map((s) => data.map((d, i) => (
          <circle
            key={`${s.key}-${i}`}
            className="analytics-point"
            style={{ fill: s.color }}
            cx={xAt(i)} cy={yAt(d[s.key])} r="2.5"
          >
            <title>{`${d[xKey]} — ${s.label}: ${formatNumber(d[s.key])}`}</title>
          </circle>
        )))}

        {data.map((d, i) => (i % labelEvery === 0 ? (
          <text
            key={`x-${i}`}
            className="analytics-axis-label"
            x={xAt(i)} y={height - 8} textAnchor="middle"
          >
            {String(d[xKey]).slice(5)}
          </text>
        ) : null))}
      </svg>

      <div className="analytics-legend">
        {series.map((s) => (
          <span key={s.key} className="analytics-legend-item">
            <span className="analytics-legend-swatch" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/** Horizontal bar list — used for feature mix, screens, exports. */
export const BarList = ({ items, valueKey = 'count', labelKey = 'label', max, emptyText = 'No data' }) => {
  if (!items || items.length === 0) {
    return <div className="analytics-empty-chart">{emptyText}</div>;
  }
  const ceiling = max || Math.max(1, ...items.map((i) => Number(i[valueKey]) || 0));

  return (
    <ul className="analytics-bar-list">
      {items.map((item, idx) => (
        <li key={item[labelKey] ?? idx} className="analytics-bar-row">
          <span className="analytics-bar-label" title={item[labelKey]}>{item[labelKey]}</span>
          <span className="analytics-bar-track">
            <span
              className="analytics-bar-fill"
              style={{ width: `${((Number(item[valueKey]) || 0) / ceiling) * 100}%` }}
            />
          </span>
          <span className="analytics-bar-value">{formatNumber(item[valueKey])}</span>
          {item.suffix ? <span className="analytics-bar-suffix">{item.suffix}</span> : null}
        </li>
      ))}
    </ul>
  );
};

/** Single headline metric. */
export const StatTile = ({ label, value, sub, tone }) => (
  <div className={`analytics-stat-tile${tone ? ` tone-${tone}` : ''}`}>
    <div className="analytics-stat-value">{value}</div>
    <div className="analytics-stat-label">{label}</div>
    {sub ? <div className="analytics-stat-sub">{sub}</div> : null}
  </div>
);
